import { useState, useEffect, useCallback, useRef } from "react";
import { Bot, TrendingUp, TrendingDown, DollarSign, RefreshCw, Play, ShoppingCart, BarChart3, AlertTriangle, ChevronRight, Zap, Target, Activity, ArrowUpRight, ArrowDownRight, Minus, Power, StopCircle, Clock, Brain, Loader2, ScrollText } from "lucide-react";
import BotPerformance from "./BotPerformance";
import BotPerformanceAudit from "./BotPerformanceAudit";
import BotAnalytics from "./BotAnalytics";
import LiveDiscoveries from "./LiveDiscoveries";
import BotAutopilot from "./BotAutopilot";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("apex_token");
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};

const SentimentBadge = ({ sentiment, score }) => {
  const cfg = sentiment === "bullish"
    ? { color: "text-emerald-400 bg-emerald-500/15 border-emerald-500/30", icon: ArrowUpRight }
    : sentiment === "bearish"
    ? { color: "text-red-400 bg-red-500/15 border-red-500/30", icon: ArrowDownRight }
    : { color: "text-slate-400 bg-slate-500/15 border-slate-500/30", icon: Minus };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded border ${cfg.color}`}>
      <Icon className="w-3 h-3" /> {sentiment?.toUpperCase()} {score != null && `(${score})`}
    </span>
  );
};

const RiskBadge = ({ level }) => {
  const color = level === "high" ? "text-red-400 bg-red-500/10" : level === "medium" ? "text-amber-400 bg-amber-500/10" : "text-emerald-400 bg-emerald-500/10";
  return <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${color}`}>RISQUE {level?.toUpperCase()}</span>;
};

export default function BotTab({ finnhubKey, onAnalyze }) {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [sentiment, setSentiment] = useState({});
  const [sentimentTicker, setSentimentTicker] = useState("");
  const [loading, setLoading] = useState({ account: false, positions: false, analysis: false, sentiment: false, order: false, bot: false });
  const [orderForm, setOrderForm] = useState({ ticker: "", quantity: 1, side: "BUY" });
  const [autoTrade, setAutoTrade] = useState(false);
  const [budget, setBudget] = useState(500);
  const [botStatus, setBotStatus] = useState(null);
  const [journal, setJournal] = useState([]);
  const [t212Connected, setT212Connected] = useState(null);
  const [apiConnected, setApiConnected] = useState(null);
  const [sessionPlan, setSessionPlan] = useState(null);
  const pollRef = useRef(null);

  const fetchSessionPlan = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/bot/session-plan`, { headers: getAuthHeaders() });
      if (r.ok) setSessionPlan(await r.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchAccount = useCallback(async () => {
    setLoading(p => ({ ...p, account: true }));
    try {
      const r = await fetch(`${API}/api/trading212/demo/account`, { headers: getAuthHeaders() });
      if (r.ok) {
        setAccount(await r.json());
        setT212Connected(true);
      } else {
        setT212Connected(false);
      }
      setApiConnected(true);
    } catch (e) {
      console.error(e);
      setApiConnected(false);
      setT212Connected(false);
    }
    setLoading(p => ({ ...p, account: false }));
  }, []);

  const fetchPositions = useCallback(async () => {
    setLoading(p => ({ ...p, positions: true }));
    try {
      const r = await fetch(`${API}/api/trading212/demo/positions`, { headers: getAuthHeaders() });
      const d = await r.json();
      setPositions(d.positions || []);
    } catch (e) { console.error(e); }
    setLoading(p => ({ ...p, positions: false }));
  }, []);

  const fetchTrades = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/trading212/demo/history`, { headers: getAuthHeaders() });
      const d = await r.json();
      setTrades(d.trades || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchBotStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/bot/status`, { headers: getAuthHeaders() });
      setBotStatus(await r.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchJournal = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/bot/journal?limit=10`, { headers: getAuthHeaders() });
      const d = await r.json();
      setJournal(d.cycles || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchAccount();
    fetchPositions();
    fetchTrades();
    fetchBotStatus();
    fetchJournal();
    fetchSessionPlan();
  }, [fetchAccount, fetchPositions, fetchTrades, fetchBotStatus, fetchJournal, fetchSessionPlan]);

  // Poll bot status every 30s when running
  useEffect(() => {
    if (botStatus?.running) {
      pollRef.current = setInterval(() => {
        fetchBotStatus();
        fetchJournal();
        fetchPositions();
        fetchAccount();
        fetchTrades();
      }, 30000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [botStatus?.running, fetchBotStatus, fetchJournal, fetchPositions, fetchAccount, fetchTrades]);

  const toggleBot = async () => {
    setLoading(p => ({ ...p, bot: true }));
    try {
      const endpoint = botStatus?.running ? "stop" : "start";
      const r = await fetch(`${API}/api/bot/${endpoint}`, { method: "POST", headers: getAuthHeaders() });
      await r.json();
      await fetchBotStatus();
      if (endpoint === "start") {
        setTimeout(() => { fetchJournal(); fetchPositions(); fetchTrades(); }, 20000);
      }
    } catch (e) { console.error(e); }
    setLoading(p => ({ ...p, bot: false }));
  };

  const runOnce = async () => {
    setLoading(p => ({ ...p, bot: true }));
    try {
      const r = await fetch(`${API}/api/bot/run-once`, { method: "POST", headers: getAuthHeaders() });
      await r.json();
      await Promise.all([fetchBotStatus(), fetchJournal(), fetchPositions(), fetchAccount(), fetchTrades()]);
    } catch (e) { console.error(e); }
    setLoading(p => ({ ...p, bot: false }));
  };

  const placeOrder = async () => {
    if (!orderForm.ticker) return;
    setLoading(p => ({ ...p, order: true }));
    try {
      const r = await fetch(`${API}/api/trading212/demo/order`, {
        method: "POST", headers: getAuthHeaders(),
        body: JSON.stringify(orderForm),
      });
      const d = await r.json();
      if (d.success) {
        setOrderForm({ ticker: "", quantity: 1, side: "BUY" });
        setTimeout(() => { fetchAccount(); fetchPositions(); fetchTrades(); }, 2000);
      } else {
        alert(`Erreur: ${d.error || "Ordre échoué"}`);
      }
    } catch (e) { alert(`Erreur: ${e.message}`); }
    setLoading(p => ({ ...p, order: false }));
  };

  const runAnalysis = async () => {
    setLoading(p => ({ ...p, analysis: true }));
    try {
      const r = await fetch(`${API}/api/trading212/bot/analyze`, {
        method: "POST", headers: getAuthHeaders(),
        body: JSON.stringify({ auto_trade: autoTrade, budget, owner: "Helmi" }),
      });
      const d = await r.json();
      setAnalysis(d);
      if (autoTrade && d.executed_trades?.length > 0) {
        setTimeout(() => { fetchAccount(); fetchPositions(); fetchTrades(); }, 3000);
      }
    } catch (e) { console.error(e); }
    setLoading(p => ({ ...p, analysis: false }));
  };

  const analyzeSentiment = async (ticker) => {
    if (!ticker) return;
    ticker = ticker.toUpperCase().trim();
    setLoading(p => ({ ...p, sentiment: true }));
    try {
      const r = await fetch(`${API}/api/sentiment/${ticker}?finnhub_key=${finnhubKey || ""}`);
      const d = await r.json();
      setSentiment(prev => ({ ...prev, [ticker]: d }));
    } catch (e) { console.error(e); }
    setLoading(p => ({ ...p, sentiment: false }));
  };

  return (
    <div className="space-y-4" data-testid="bot-tab">

      {/* DATA-DRIVEN AUDIT — top of bot tab */}
      <BotPerformanceAudit />
      {/* CONNECTION STATUS BANNER */}
      <div className="glass-card rounded-xl p-3 border border-slate-700/30 flex items-center justify-between flex-wrap gap-2" data-testid="connection-status-banner">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${apiConnected === true ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]" : apiConnected === false ? "bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.5)]" : "bg-slate-600 animate-pulse"}`} />
            <span className="text-[10px] font-bold text-slate-400">API</span>
            <span className={`text-[10px] font-bold ${apiConnected === true ? "text-emerald-400" : apiConnected === false ? "text-red-400" : "text-slate-600"}`}>
              {apiConnected === true ? "OK" : apiConnected === false ? "HORS LIGNE" : "..."}
            </span>
          </div>
          <div className="w-px h-4 bg-slate-700/50" />
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${t212Connected === true ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]" : t212Connected === false ? "bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.5)]" : "bg-slate-600 animate-pulse"}`} />
            <span className="text-[10px] font-bold text-slate-400">T212 DEMO</span>
            <span className={`text-[10px] font-bold ${t212Connected === true ? "text-emerald-400" : t212Connected === false ? "text-red-400" : "text-slate-600"}`}>
              {t212Connected === true ? "CONNECTÉ" : t212Connected === false ? "DÉCONNECTÉ" : "..."}
            </span>
          </div>
          <div className="w-px h-4 bg-slate-700/50" />
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${botStatus?.running ? "bg-emerald-400 animate-pulse shadow-[0_0_6px_rgba(52,211,153,0.5)]" : "bg-slate-600"}`} />
            <span className="text-[10px] font-bold text-slate-400">BOT</span>
            <span className={`text-[10px] font-bold ${botStatus?.running ? "text-emerald-400" : "text-slate-600"}`}>
              {botStatus?.running ? "ACTIF" : "ARRÊTÉ"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-bold">AUTO-RESTART</span>
          {botStatus?.instance_id && (
            <span
              className={`text-[9px] px-2 py-0.5 rounded font-mono font-bold border ${
                botStatus.instance_id.startsWith("production")
                  ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                  : "bg-amber-500/10 text-amber-400 border-amber-500/20"
              }`}
              title={`Bot instance — ${botStatus.auto_start ? "auto-start ON" : "auto-start OFF"}`}
              data-testid="bot-instance-id"
            >
              {botStatus.instance_id}
            </span>
          )}
          {account && <span className="text-[9px] text-slate-600 font-mono">{account.total_value?.toLocaleString("fr-FR")}€</span>}
        </div>
      </div>

      {/* BOT CAPITAL OVERVIEW — P&L since inception */}
      {account && (
        <div className="glass-card rounded-xl p-4 border border-slate-700/30 bg-gradient-to-r from-slate-900/80 to-slate-800/40" data-testid="bot-capital-overview">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-5 h-5 text-amber-400" />
            <h3 className="text-sm font-black text-white uppercase tracking-wider">Capital du Bot</h3>
            <span className="text-[9px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded font-bold">DEMO</span>
            {account.from_cache && <span className="text-[8px] text-slate-600 italic">cache</span>}
          </div>
          {(() => {
            const startingCapital = account.starting_capital || 50000;
            const currentValue = account.total_value || 0;
            const totalPnl = account.total_pnl_from_start != null ? account.total_pnl_from_start : (currentValue - startingCapital);
            const totalPnlPct = account.total_pnl_pct != null ? account.total_pnl_pct : ((totalPnl / startingCapital) * 100);
            const unrealizedPnl = account.pnl || 0;
            const realizedPnl = account.realized_pnl || 0;
            const isPositive = totalPnl >= 0;
            return (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/20">
                  <div className="text-[9px] text-slate-500 mb-1">CAPITAL INITIAL</div>
                  <div className="text-lg font-black text-slate-400">{startingCapital.toLocaleString("fr-FR")}€</div>
                </div>
                <div className={`rounded-lg p-3 border ${isPositive ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"}`}>
                  <div className="text-[9px] text-slate-500 mb-1">CAPITAL ACTUEL</div>
                  <div className={`text-lg font-black ${isPositive ? "text-emerald-400" : "text-white"}`}>{currentValue.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€</div>
                </div>
                <div className={`rounded-lg p-3 border ${isPositive ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"}`}>
                  <div className="text-[9px] text-slate-500 mb-1">P&L TOTAL</div>
                  <div className={`text-lg font-black ${isPositive ? "text-emerald-400" : "text-red-400"}`}>
                    {isPositive ? "+" : ""}{totalPnl.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€
                  </div>
                  <div className={`text-[10px] font-bold ${isPositive ? "text-emerald-500/70" : "text-red-500/70"}`}>
                    {isPositive ? "+" : ""}{totalPnlPct.toFixed(2)}%
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/20">
                  <div className="text-[9px] text-slate-500 mb-1">NON-RÉALISÉ</div>
                  <div className={`text-lg font-black ${unrealizedPnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {unrealizedPnl >= 0 ? "+" : ""}{unrealizedPnl.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€
                  </div>
                  <div className="text-[9px] text-slate-600">Positions ouvertes</div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/20">
                  <div className="text-[9px] text-slate-500 mb-1">RÉALISÉ</div>
                  <div className={`text-lg font-black ${realizedPnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {realizedPnl >= 0 ? "+" : ""}{realizedPnl.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€
                  </div>
                  <div className="text-[9px] text-slate-600">Trades clos</div>
                </div>
              </div>
            );
          })()}
          {/* Progress bar showing capital preservation */}
          {(() => {
            const startingCapital = account.starting_capital || 50000;
            const currentValue = account.total_value || 0;
            const pct = (currentValue / startingCapital) * 100;
            const isPositive = pct >= 100;
            return (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] text-slate-500">Préservation du capital</span>
                  <span className={`text-[10px] font-bold ${isPositive ? "text-emerald-400" : pct >= 98 ? "text-amber-400" : "text-red-400"}`}>{pct.toFixed(1)}%</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${isPositive ? "bg-emerald-500" : pct >= 98 ? "bg-amber-500" : "bg-red-500"}`}
                    style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* BOT ANALYTICS — Diagnostics, Recap, Metrics */}
      <BotAnalytics />

      {/* LIVE DISCOVERY ENGINE — Real-time top movers + news catalysts */}
      <LiveDiscoveries />

      {/* AUTOPILOT — Self-monitoring, error detection, auto-correction */}
      <BotAutopilot />

      {/* AUTONOMOUS BOT CONTROL */}
      <div className={`glass-card rounded-xl p-4 border ${botStatus?.running ? "border-emerald-500/40 bg-emerald-500/[0.03]" : "border-slate-700/30"} relative overflow-hidden`} data-testid="autonomous-bot-control">
        {botStatus?.running && <div className="absolute top-0 left-0 right-0 h-0.5 bg-emerald-500 animate-pulse" />}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${botStatus?.running ? "bg-emerald-500/20" : "bg-slate-800/60"}`}>
              <Brain className={`w-5 h-5 ${botStatus?.running ? "text-emerald-400 animate-pulse" : "text-slate-500"}`} />
            </div>
            <div>
              <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                Bot Autonome H24
                {botStatus?.running ? (
                  <span className="text-[9px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full font-bold animate-pulse">EN COURS</span>
                ) : (
                  <span className="text-[9px] px-2 py-0.5 bg-slate-700/50 text-slate-500 rounded-full font-bold">ARRÊTÉ</span>
                )}
              </h3>
              <p className="text-[10px] text-slate-500">
                GPT-5.4 · v5 Active Trader · {botStatus?.cycle_normal_sec ? `${botStatus.cycle_normal_sec/60}min` : "5min"} ({botStatus?.cycle_opening_sec ? `${botStatus.cycle_opening_sec/60}min` : "1min"} open) · Max {botStatus?.max_positions || 6} pos · Cash {botStatus?.cash_reserve_pct || 15}% min · R:R {botStatus?.min_reward_risk || 1.2}:1 · Min 2000€/pos
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runOnce} disabled={loading.bot || botStatus?.running}
              className="px-3 py-2 bg-slate-800/60 border border-slate-700/30 rounded-lg text-[10px] font-bold text-slate-400 hover:text-cyan-400 hover:border-cyan-500/30 transition-all disabled:opacity-30"
              data-testid="run-once-btn" title="Exécuter 1 cycle">
              {loading.bot && !botStatus?.running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
            </button>
            <button onClick={toggleBot} disabled={loading.bot}
              className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${
                botStatus?.running
                  ? "bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/30"
                  : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/30"
              }`} data-testid="toggle-bot-btn">
              {loading.bot ? <Loader2 className="w-3 h-3 animate-spin" /> : botStatus?.running ? <StopCircle className="w-3 h-3" /> : <Power className="w-3 h-3" />}
              {botStatus?.running ? "ARRÊTER" : "DÉMARRER"}
            </button>
          </div>
        </div>
        {/* Bot Stats */}
        {botStatus && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-slate-800/30 rounded-lg p-2 text-center">
                <div className="text-[9px] text-slate-500">CYCLES</div>
                <div className="text-sm font-bold text-white">{botStatus.cycles_completed}</div>
              </div>
              <div className="bg-slate-800/30 rounded-lg p-2 text-center">
                <div className="text-[9px] text-slate-500">TRADES</div>
                <div className="text-sm font-bold text-cyan-400">{botStatus.total_trades}</div>
              </div>
              <div className="bg-slate-800/30 rounded-lg p-2 text-center">
                <div className="text-[9px] text-slate-500">UNIVERS</div>
                <div className="text-sm font-bold text-white">{botStatus.universe_size}</div>
              </div>
              <div className="bg-slate-800/30 rounded-lg p-2 text-center">
                <div className="text-[9px] text-slate-500">DERNIER CYCLE</div>
                <div className="text-[10px] font-mono text-slate-400">{botStatus.last_cycle_at ? new Date(botStatus.last_cycle_at).toLocaleTimeString("fr-FR") : "—"}</div>
              </div>
            </div>
            {/* v4 Mode Slots & Daily State */}
            {botStatus.mode_config && (
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(botStatus.mode_config).map(([mode, cfg]) => {
                  const paused = botStatus.daily_state?.mode_paused?.[mode];
                  return (
                    <div key={mode} className={`rounded-lg p-2 text-center border ${paused ? "bg-red-500/5 border-red-500/20" : "bg-slate-800/30 border-slate-700/20"}`}>
                      <div className="text-[8px] text-slate-500 truncate">{mode.replace(/_/g, " ")}</div>
                      <div className={`text-xs font-bold ${paused ? "text-red-400" : "text-white"}`}>
                        {paused ? "PAUSE" : `${cfg.max} slots`}
                      </div>
                      <div className="text-[8px] text-slate-600">{cfg.size}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {botStatus.daily_state?.halted && (
              <div className="p-2 bg-red-500/10 border border-red-500/20 rounded-lg text-[10px] text-red-400 font-bold text-center">
                TRADING HALTE — Limite drawdown/pertes atteinte
              </div>
            )}
          </div>
        )}
        {botStatus?.last_error && (
          <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg text-[10px] text-red-400">
            <AlertTriangle className="w-3 h-3 inline mr-1" /> {botStatus.last_error}
          </div>
        )}
      </div>

      {/* PRE-SESSION PLAN — Trading targets with key levels */}
      {sessionPlan?.targets?.length > 0 && (
        <div className="glass-card rounded-xl p-4 border border-amber-500/20" data-testid="session-plan">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-2">
              <Target className="w-4 h-4 text-amber-400" />
              Plan Pre-Session {sessionPlan.market}
              <span className="text-[9px] px-2 py-0.5 bg-amber-500/15 text-amber-400 rounded-full font-bold">{sessionPlan.targets_count} targets</span>
            </h4>
            <span className="text-[9px] text-slate-600 font-mono">
              {sessionPlan.prepared_at ? new Date(sessionPlan.prepared_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "—"}
            </span>
          </div>
          <div className="space-y-1.5">
            {sessionPlan.targets.map((t, i) => {
              const signal = t.ta_signal || "";
              const sigColor = signal === "GOOD_ENTRY" ? "text-emerald-400 bg-emerald-500/15 border-emerald-500/25"
                : signal === "ACCEPTABLE" ? "text-cyan-400 bg-cyan-500/15 border-cyan-500/25"
                : signal === "CAUTION" ? "text-amber-400 bg-amber-500/15 border-amber-500/25"
                : signal === "DANGER" ? "text-red-400 bg-red-500/15 border-red-500/25"
                : "text-slate-400 bg-slate-500/10 border-slate-500/20";
              const srcBadge = t.source === "PREV_DAY_MOVER" ? "text-violet-400 bg-violet-500/15"
                : t.source === "ROCKET" ? "text-orange-400 bg-orange-500/15"
                : t.source === "PREMARKET" ? "text-cyan-400 bg-cyan-500/15"
                : "text-slate-400 bg-slate-500/10";
              return (
                <div key={i} className="p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/20 hover:border-amber-500/20 transition-all cursor-pointer"
                  onClick={() => onAnalyze?.(t.ticker)} data-testid={`session-target-${t.ticker}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[11px] font-black text-amber-400 w-4">{i + 1}</span>
                    <span className="font-mono text-xs font-bold text-cyan-400">{t.ticker}</span>
                    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${srcBadge}`}>{t.source?.replace(/_/g, " ")}</span>
                    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${sigColor}`}>{signal.replace(/_/g, " ")}</span>
                    <span className={`text-[10px] font-bold ml-auto ${t.change_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      +{t.change_pct?.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px]">
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Pivot:</span>
                      <span className="text-white font-mono font-bold">${t.pivot?.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Break:</span>
                      <span className="text-amber-400 font-mono font-bold">${t.breakout?.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Stop:</span>
                      <span className="text-red-400 font-mono font-bold">${t.stop_loss_level?.toFixed(2)}</span>
                    </div>
                    <span className="text-[9px] text-slate-600 ml-auto">RSI {t.ta_rsi?.toFixed(0)}</span>
                  </div>
                  {t.targets?.length > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-[9px] text-slate-500">Targets:</span>
                      {t.targets.slice(0, 4).map((tgt, j) => (
                        <span key={j} className="text-[9px] font-mono text-emerald-400/80">${tgt?.toFixed(2)}</span>
                      ))}
                      {t.targets.length > 4 && <span className="text-[9px] text-emerald-400/50">+</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* BOT PERFORMANCE DASHBOARD */}
      <BotPerformance onAnalyze={onAnalyze} positions={positions} account={account} />

      {/* BOT JOURNAL - AI Decision Log */}
      {journal.length > 0 && (
        <div className="glass-card rounded-xl p-4 border border-violet-500/20" data-testid="bot-journal">
          <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-violet-400" /> JOURNAL DU BOT ({journal.length} cycles)
          </h4>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {journal.map((cycle, i) => (
              <div key={i} className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-violet-400">Cycle #{cycle.cycle_number}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                      cycle.portfolio_health === "bon" ? "bg-emerald-500/15 text-emerald-400" :
                      cycle.portfolio_health === "danger" ? "bg-red-500/15 text-red-400" :
                      "bg-amber-500/15 text-amber-400"
                    }`}>{cycle.portfolio_health || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[9px] text-slate-600">
                    <Clock className="w-3 h-3" />
                    {cycle.timestamp ? new Date(cycle.timestamp).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "—"}
                    <span className="text-slate-700">({cycle.duration_sec?.toFixed(0)}s)</span>
                  </div>
                </div>
                {cycle.market_analysis && (
                  <p className="text-[11px] text-slate-400 mb-2">{cycle.market_analysis}</p>
                )}
                {cycle.ai_decision?.actions?.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {cycle.ai_decision.actions.map((a, j) => (
                      <div key={j} className="flex items-center gap-2 text-[10px]">
                        <span className={`font-bold px-1.5 py-0.5 rounded ${
                          a.type === "BUY" ? "bg-emerald-500/15 text-emerald-400" :
                          a.type === "SELL" ? "bg-red-500/15 text-red-400" :
                          "bg-slate-500/15 text-slate-400"
                        }`}>{a.type}</span>
                        <span className="font-mono text-cyan-400 cursor-pointer hover:text-cyan-300" onClick={() => onAnalyze?.(a.ticker)}>{a.ticker}</span>
                        <span className="text-slate-500">x{a.quantity}</span>
                        <span className="text-slate-400 flex-1">{a.reason}</span>
                        <span className="text-white font-bold">{a.confidence}%</span>
                        {a.skipped && <span className="text-amber-400">{a.skip_reason}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {cycle.ai_decision?.actions?.length === 0 && (
                  <p className="text-[10px] text-slate-600 italic">Pas d'action — le bot attend une meilleure opportunité</p>
                )}
                {cycle.trades_executed?.length > 0 && (
                  <div className="mt-1 pt-1 border-t border-slate-700/20">
                    {cycle.trades_executed.map((t, k) => (
                      <div key={k} className={`text-[10px] ${t.status === "success" ? "text-emerald-400" : "text-red-400"}`}>
                        {t.status === "success" ? "✓" : "✗"} {t.side} {t.quantity}x {t.ticker} — {t.reason}
                      </div>
                    ))}
                  </div>
                )}
                {cycle.next_focus && (
                  <div className="mt-1 text-[9px] text-slate-600">
                    <Target className="w-2.5 h-2.5 inline mr-1" /> Prochain: {cycle.next_focus}
                  </div>
                )}
                {cycle.errors?.length > 0 && (
                  <div className="mt-1 text-[9px] text-red-400">{cycle.errors.join(", ")}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* ACCOUNT SUMMARY */}
      <div className="glass-card rounded-xl p-4 border border-cyan-500/20" data-testid="demo-account-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-cyan-400" />
            <h3 className="text-sm font-black text-white uppercase tracking-wider">Bot Trading T212</h3>
            <span className="text-[9px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded font-bold">DEMO</span>
          </div>
          <button onClick={() => { fetchAccount(); fetchPositions(); fetchTrades(); }}
            className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-cyan-400 transition-colors" data-testid="refresh-bot">
            <RefreshCw className={`w-4 h-4 ${loading.account ? "animate-spin" : ""}`} />
          </button>
        </div>
        {account && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-slate-800/40 rounded-lg p-3 border border-slate-700/30">
              <div className="text-[10px] text-slate-500 mb-1">VALEUR TOTALE</div>
              <div className="text-lg font-black text-white">{account.total_value?.toLocaleString("fr-FR")} {account.currency}</div>
            </div>
            <div className="bg-slate-800/40 rounded-lg p-3 border border-slate-700/30">
              <div className="text-[10px] text-slate-500 mb-1">CASH DISPO</div>
              <div className="text-lg font-black text-emerald-400">{account.cash?.toLocaleString("fr-FR")} {account.currency}</div>
            </div>
            <div className="bg-slate-800/40 rounded-lg p-3 border border-slate-700/30">
              <div className="text-[10px] text-slate-500 mb-1">INVESTI</div>
              <div className="text-lg font-black text-cyan-400">{account.invested?.toLocaleString("fr-FR")} {account.currency}</div>
            </div>
            <div className="bg-slate-800/40 rounded-lg p-3 border border-slate-700/30">
              <div className="text-[10px] text-slate-500 mb-1">P&L</div>
              <div className={`text-lg font-black ${account.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {account.pnl >= 0 ? "+" : ""}{account.pnl?.toLocaleString("fr-FR")} {account.currency}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ORDER FORM + AI ANALYSIS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Manual Order */}
        <div className="glass-card rounded-xl p-4 border border-slate-700/30" data-testid="order-form">
          <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-cyan-400" /> ORDRE MANUEL
          </h4>
          <div className="flex gap-2 mb-3">
            <input value={orderForm.ticker} onChange={e => setOrderForm(p => ({ ...p, ticker: e.target.value.toUpperCase() }))}
              placeholder="Ticker (ex: NVDA)" className="flex-1 bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-cyan-500/50 focus:outline-none"
              data-testid="order-ticker-input" />
            <input type="number" value={orderForm.quantity} onChange={e => setOrderForm(p => ({ ...p, quantity: parseFloat(e.target.value) || 0 }))}
              min="0.01" step="0.01" className="w-20 bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500/50 focus:outline-none"
              data-testid="order-quantity-input" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setOrderForm(p => ({ ...p, side: "BUY" }))}
              className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${orderForm.side === "BUY" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-slate-800/40 text-slate-500 border border-slate-700/30"}`}
              data-testid="order-buy-btn">BUY</button>
            <button onClick={() => setOrderForm(p => ({ ...p, side: "SELL" }))}
              className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${orderForm.side === "SELL" ? "bg-red-500/20 text-red-400 border border-red-500/40" : "bg-slate-800/40 text-slate-500 border border-slate-700/30"}`}
              data-testid="order-sell-btn">SELL</button>
            <button onClick={placeOrder} disabled={loading.order || !orderForm.ticker}
              className="px-4 py-2 bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 rounded-lg text-xs font-bold hover:bg-cyan-500/30 transition-all disabled:opacity-40"
              data-testid="order-submit-btn">
              {loading.order ? "..." : "EXÉCUTER"}
            </button>
          </div>
        </div>

        {/* AI Bot Analysis */}
        <div className="glass-card rounded-xl p-4 border border-violet-500/20" data-testid="ai-bot-analysis">
          <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-violet-400" /> ANALYSE IA & AUTO-TRADE
          </h4>
          <div className="flex items-center gap-3 mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={autoTrade} onChange={e => setAutoTrade(e.target.checked)}
                className="rounded border-slate-700 bg-slate-800 text-violet-500 focus:ring-violet-500" data-testid="auto-trade-toggle" />
              <span className="text-[10px] text-slate-400">Auto-trade</span>
            </label>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-slate-500">Budget:</span>
              <input type="number" value={budget} onChange={e => setBudget(parseInt(e.target.value) || 0)}
                className="w-16 bg-slate-800/60 border border-slate-700/50 rounded px-2 py-1 text-[11px] text-white focus:outline-none" />
              <span className="text-[10px] text-slate-500">EUR</span>
            </div>
          </div>
          <button onClick={runAnalysis} disabled={loading.analysis}
            className="w-full py-2.5 bg-violet-500/20 text-violet-400 border border-violet-500/40 rounded-lg text-xs font-bold hover:bg-violet-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="run-analysis-btn">
            {loading.analysis ? (
              <><RefreshCw className="w-3 h-3 animate-spin" /> Analyse en cours...</>
            ) : (
              <><Play className="w-3 h-3" /> {autoTrade ? "ANALYSER & TRADER" : "ANALYSER SIGNAUX"}</>
            )}
          </button>
        </div>
      </div>

      {/* AI Analysis Results */}
      {analysis && (
        <div className="glass-card rounded-xl p-4 border border-violet-500/20" data-testid="analysis-results">
          <h4 className="text-xs font-bold text-white mb-3">RÉSULTATS DE L'ANALYSE</h4>
          <p className="text-xs text-slate-400 mb-3">{analysis.analysis?.market_outlook}</p>
          {analysis.analysis?.recommendations?.map((rec, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/20 mb-2">
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${rec.action === "BUY" ? "bg-emerald-500/20 text-emerald-400" : rec.action === "SELL" ? "bg-red-500/20 text-red-400" : "bg-slate-500/20 text-slate-400"}`}>
                {rec.action}
              </span>
              <span className="font-mono text-sm font-bold text-cyan-400 cursor-pointer hover:text-cyan-300" onClick={() => onAnalyze?.(rec.ticker)}>
                {rec.ticker}
              </span>
              <span className="text-[10px] text-slate-500">x{rec.quantity}</span>
              <span className="text-[10px] text-slate-400 flex-1">{rec.reason}</span>
              <span className="text-[10px] font-bold text-white">{rec.confidence}%</span>
              {rec.skipped && <span className="text-[9px] text-amber-400">{rec.skip_reason}</span>}
            </div>
          ))}
          {analysis.executed_trades?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-700/20">
              <h5 className="text-[10px] font-bold text-emerald-400 mb-2">TRADES EXÉCUTÉS</h5>
              {analysis.executed_trades.map((t, i) => (
                <div key={i} className={`text-[10px] ${t.success ? "text-emerald-400" : "text-red-400"}`}>
                  {t.success ? "✓" : "✗"} {t.side} {t.quantity}x {t.ticker}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SENTIMENT ANALYSIS */}
      <div className="glass-card rounded-xl p-4 border border-amber-500/20" data-testid="sentiment-section">
        <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-amber-400" /> ANALYSE DE SENTIMENT
        </h4>
        <div className="flex gap-2 mb-3">
          <input value={sentimentTicker} onChange={e => setSentimentTicker(e.target.value.toUpperCase())}
            placeholder="Ticker (ex: NVDA, MSFT, META)" onKeyDown={e => e.key === "Enter" && analyzeSentiment(sentimentTicker)}
            className="flex-1 bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-amber-500/50 focus:outline-none"
            data-testid="sentiment-ticker-input" />
          <button onClick={() => analyzeSentiment(sentimentTicker)} disabled={loading.sentiment || !sentimentTicker}
            className="px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/40 rounded-lg text-xs font-bold hover:bg-amber-500/30 transition-all disabled:opacity-40"
            data-testid="sentiment-analyze-btn">
            {loading.sentiment ? <RefreshCw className="w-3 h-3 animate-spin" /> : <BarChart3 className="w-3 h-3" />}
          </button>
        </div>
        {/* Quick sentiment buttons for portfolio */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {["NVDA", "MSFT", "AMZN", "META", "ASML", "SAP"].map(t => (
            <button key={t} onClick={() => { setSentimentTicker(t); analyzeSentiment(t); }}
              className="px-2 py-1 bg-slate-800/40 border border-slate-700/30 rounded text-[10px] font-mono text-slate-400 hover:text-amber-400 hover:border-amber-500/30 transition-all"
              data-testid={`sentiment-quick-${t}`}>
              {t} {sentiment[t] && <SentimentBadge sentiment={sentiment[t].sentiment} score={sentiment[t].score} />}
            </button>
          ))}
        </div>

        {/* Sentiment Results */}
        {Object.entries(sentiment).filter(([, v]) => v?.sentiment).map(([ticker, data]) => (
          <div key={ticker} className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/20 mb-2" data-testid={`sentiment-result-${ticker}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold text-cyan-400 cursor-pointer hover:text-cyan-300" onClick={() => onAnalyze?.(ticker)}>{ticker}</span>
                <SentimentBadge sentiment={data.sentiment} score={data.score} />
                <RiskBadge level={data.risk_level} />
              </div>
              <span className="text-[9px] text-slate-600">{data.news_count} news · conf {data.confidence}%</span>
            </div>
            <p className="text-xs text-slate-400 mb-2">{data.summary}</p>
            {data.signals?.length > 0 && (
              <div className="space-y-1">
                {data.signals.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px]">
                    <span className={s.type === "positive" ? "text-emerald-400" : s.type === "negative" ? "text-red-400" : "text-slate-500"}>
                      {s.type === "positive" ? "▲" : s.type === "negative" ? "▼" : "•"}
                    </span>
                    <span className="text-slate-400">{s.text}</span>
                  </div>
                ))}
              </div>
            )}
            {data.institutional_activity && (
              <div className="mt-2 pt-2 border-t border-slate-700/20 text-[10px] text-violet-400">
                <span className="font-bold">INSTITUTIONNEL:</span> {data.institutional_activity}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* DEMO POSITIONS */}
      {positions.length > 0 && (
        <div className="glass-card rounded-xl p-4 border border-slate-700/30" data-testid="demo-positions">
          <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-cyan-400" /> POSITIONS DÉMO ({positions.length})
          </h4>
          <div className="space-y-1.5">
            {positions.map((p, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/30 border border-slate-700/20 hover:border-cyan-500/20 transition-all">
                <span className="font-mono text-xs font-bold text-cyan-400 w-16 cursor-pointer hover:text-cyan-300" onClick={() => onAnalyze?.(p.ticker)}>{p.ticker}</span>
                <span className="text-[10px] text-slate-500 flex-1 truncate">{p.name}</span>
                <span className="text-[10px] text-white font-mono">x{p.quantity}</span>
                <span className="text-[10px] text-slate-400 font-mono">{p.current_price}</span>
                <span className={`text-[10px] font-bold font-mono ${p.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {p.pnl >= 0 ? "+" : ""}{p.pnl?.toFixed(2)}
                </span>
                <button onClick={() => { setSentimentTicker(p.ticker); analyzeSentiment(p.ticker); }}
                  className="p-1 text-slate-600 hover:text-amber-400 transition-colors" title="Analyser sentiment">
                  <Activity className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TRADE HISTORY */}
      {trades.length > 0 && (
        <div className="glass-card rounded-xl p-4 border border-slate-700/30" data-testid="trade-history">
          <h4 className="text-xs font-bold text-white mb-3">HISTORIQUE DES TRADES ({trades.length})</h4>
          <div className="space-y-1">
            {trades.slice(0, 15).map((t, i) => (
              <div key={i} className="flex items-center gap-3 text-[10px] py-1.5 border-b border-slate-700/10">
                <span className={`font-bold px-1.5 py-0.5 rounded ${t.side === "BUY" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{t.side}</span>
                <span className="font-mono text-cyan-400">{t.ticker}</span>
                <span className="text-slate-500">x{t.quantity}</span>
                <span className={t.status === "success" ? "text-emerald-400" : "text-red-400"}>{t.status}</span>
                <span className="text-slate-600 ml-auto">{t.source || "manual"}</span>
                <span className="text-slate-600">{t.timestamp?.slice(0, 16)?.replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
